import { inject, singleton } from "tsyringe";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { Snippets } from "../../common/snippets";
import { CommonModule } from "../common.module";
import { ApiModule } from "../common/api/api.module";
import { CreateSpreadSheetRequestDTO, ErrorTypes, SpreadSheetEnabledInputInfoDTO, TokenInfoDTO, UpdateSpreadSheetRequestDTO } from "../common/api/autogenerated";
import { UrlChangeListenerModule } from "../common/url-change-listener.module";
import { StateManager } from "./state-manager";
import GoogleSheetExport from './GoogleSheetExport.vue';
import { UiModule } from "../common/ui.module";

@singleton()
export class GoogleSheetExportModule extends Module {
    public name: string = 'GoogleSheetExportModule';
    public acceptedLicenseFlags: string[] = ['GoogleSpreadSheetExport'];
    public shouldRun(context: ExtensionContext): boolean {
        return context === ExtensionContext.vizportal;
    }
    constructor(
        @inject(CommonModule) private common: CommonModule,
        @inject(ApiModule) private apiModule: ApiModule,
        @inject(UrlChangeListenerModule) private urlChangeModule: UrlChangeListenerModule,@inject(UiModule) private uiModule: UiModule) {
        super([common, apiModule, urlChangeModule, uiModule]);
    }
    private ids = {
        downloadButton: 'google-sheet-download',
        downloadButtonIcon: 'google-sheet-icon',
        downloadButtonLoading: 'google-sheet-loading',
    }
    private gSheetDownloadButtonTemplate = `<div class="f16dpfvc" style="height: 30px;line-height: 30px;">
        <div class="f1f1ygnm">
            <span id="${this.ids.downloadButtonIcon}" style="width: 18px;height: 18px;flex-grow: 0;flex-shrink: 0;justify-content: center;margin-right: 4px;padding-left: 3px;">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="padding-top: 5px;width: 100%;height: 100%;">
                    <path fill="rgb(102, 102, 102)" d="M19,11V9H11V5H9V9H5V11H9V19H11V11H19M19,3C19.5,3 20,3.2 20.39,3.61C20.8,4 21,4.5 21,5V19C21,19.5 20.8,20 20.39,20.39C20,20.8 19.5,21 19,21H5C4.5,21 4,20.8 3.61,20.39C3.2,20 3,19.5 3,19V5C3,4.5 3.2,4 3.61,3.61C4,3.2 4.5,3 5,3H19Z"></path>
                </svg>
            </span>
            <span id="${this.ids.downloadButtonLoading}" class="loading" style="display: inline-block; transform-origin: top left; transform: scale(0.6); vertical-align: middle; height: 14px; width: 18px; display: none;">${Snippets.loadingGif}</span>
            <label class="f13b9dl4">Google Sheets</label>
        </div>
    </div>`;
    public async run(): Promise<void> {
        // check the feature license and then check the feature is enabled or not
        const api = await this.apiModule.apiRequest(c => c.apiGoogleSheetInfo(new SpreadSheetEnabledInputInfoDTO({
                        serverId: window.stm.config.serverId,
                    })));
        if (!api.success) {
            const errorType = api.error?.error;
            if (errorType === ErrorTypes.LicenseFeatureDisabled || errorType === ErrorTypes.SheetIntegrationDisabled) {
                return; // do not show button if license or feature is disabled
            }
        }

        // add an Export to Google Sheet button to the download button in Tableau
        this.urlChangeModule.event.subscribe(async () => {
            // find the vizql iframe
            const vizqlframe = await this.common.waitForElement('iframe', 20, window.document) as HTMLIFrameElement;
            if (!vizqlframe) {
                return;
            }
            let document = vizqlframe.contentDocument!;
            // find the download button
            const viewerToolbarSection = (await this.common.waitForElement('#viz-viewer-toolbar', 60, document)) as HTMLElement;
            if (!viewerToolbarSection) {
                return;
            }
            // when the button is clicked, wait an animation frame, then find the download menu
            viewerToolbarSection.onclick = async () => {
                await this.common.requestAnimationFrame();
                const downloadMenu = await this.common.waitForElement('#viz-viewer-toolbar-download-menu', 2, document);
                if (!downloadMenu) {
                    return;
                }
                // check through all items in the menu. if our button exists, skip
                let gSheetButtonExists = false;
                downloadMenu.childNodes.forEach(e => {
                    const c = e as HTMLElement;
                    if (!c) {
                        return;
                    }
                    if (c.id === this.ids.downloadButton) {
                        gSheetButtonExists = true;
                    }
                })
                if (gSheetButtonExists) {
                    return;
                }

                const el = document.createElement('div');
                el.id = this.ids.downloadButton;
                el.innerHTML = this.gSheetDownloadButtonTemplate;
                downloadMenu.appendChild(el);
                // handle mousover / mouseleave highlight
                el.onmouseenter = () => {
                    downloadMenu.childNodes.forEach(e => {
                        const c = e as HTMLElement;
                        if (!c || c.id === this.ids.downloadButton) {
                            return;
                        }
                        c.children[0].classList.remove('highlight');
                    })
                    el.children[0].classList.add('highlight');
                }
                el.onmouseleave = () => el.children[0].classList.remove('highlight');
                el.onclick = async () => {
                    const buttonIcon = document.getElementById(this.ids.downloadButtonIcon) as HTMLElement;
                    const buttonLoading = document.getElementById(this.ids.downloadButtonLoading) as HTMLElement;
                    try {
                        buttonIcon.style.display = 'none';
                        buttonLoading.style.display = 'inline-block';
                        this.uiModule.injectUI(GoogleSheetExport);
                    } catch (e) {
                        this.log.e('Failed to open popup', e);
                        alert('Failed to open Export Google Sheet popup');
                    } finally {
                        buttonIcon.style.display = 'inline-block';
                        buttonLoading.style.display = 'none';
                    }

                }
            };

        });
    }

  
}


